import type { Tab } from './index';
import { renderMarkdown, timeAgo, esc } from '../utils';
import { workerFetch, fetchSheet } from '../worker';
import { fetchAlpacaNews } from '../alpaca';
import { store } from '../state';

interface BriefResponse {
  brief?:        string;
  generatedAt?:  string;
  note?:         string;
  error?:        string;
}

type Panel = 'brief' | 'news';

export class BriefTab implements Tab {
  readonly id = 'brief';

  private newsFetched = false;   // session cache flag — never re-fetch news

  private el(id: string): HTMLElement {
    return document.getElementById(id) as HTMLElement;
  }

  init(): void {
    this.el('brief-refresh-btn').addEventListener('click', () => this.load());
    this.el('brief-run-btn').addEventListener('click',     () => this.run());
    this.el('brief-empty-run-btn').addEventListener('click', () => this.run());
    this.el('brief-tab-brief').addEventListener('click', () => this.showPanel('brief'));
    this.el('brief-tab-news').addEventListener('click',  () => this.showPanel('news'));
  }

  render(): void {
    const cached = store.get('briefData');
    if (cached) {
      this.displayBrief(cached.brief, cached.generatedAt);
    } else {
      this.load();
    }
  }

  private showPanel(which: Panel): void {
    this.el('brief-panel-brief').style.display = which === 'brief' ? '' : 'none';
    this.el('brief-panel-news').style.display  = which === 'news'  ? '' : 'none';
    this.el('brief-tab-brief').classList.toggle('active', which === 'brief');
    this.el('brief-tab-news').classList.toggle('active',  which === 'news');
    if (which === 'news') this.loadNews();
  }

  private async load(): Promise<void> {
    if (!this.checkWorker()) return;
    const btn = this.el('brief-refresh-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = '↺ LOADING…';
    try {
      const data = await workerFetch<BriefResponse>('/api/brief');
      if (data.note || !data.brief) {
        this.el('brief-meta').innerHTML = '<span>No brief generated yet</span>';
        this.el('brief-body').style.display  = 'none';
        this.el('brief-empty').style.display = '';
      } else {
        this.displayBrief(data.brief, data.generatedAt);
        store.set('briefData', { date: '', brief: data.brief, generatedAt: data.generatedAt ?? '' });
      }
    } catch (err: unknown) {
      this.el('brief-meta').textContent = 'Error loading brief: ' + (err instanceof Error ? err.message : String(err));
    } finally {
      btn.disabled = false;
      btn.textContent = '↺ REFRESH';
    }
  }

  private async run(): Promise<void> {
    if (!this.checkWorker(true)) return;
    const btn = this.el('brief-run-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = '⟳ GENERATING…';
    this.el('brief-meta').innerHTML    = 'Running agents — this takes ~20 seconds…';
    this.el('brief-body').style.display  = 'none';
    this.el('brief-empty').style.display = 'none';
    try {
      const data = await workerFetch<BriefResponse>('/api/brief', { method: 'POST', signal: AbortSignal.timeout(60000) });
      if (data.error) throw new Error(data.error);
      if (!data.brief) throw new Error('Empty response from server');
      this.displayBrief(data.brief, data.generatedAt);
      store.set('briefData', { date: '', brief: data.brief, generatedAt: data.generatedAt ?? '' });
    } catch (err: unknown) {
      this.el('brief-meta').textContent = 'Error: ' + (err instanceof Error ? err.message : String(err));
      this.el('brief-empty').style.display = '';
    } finally {
      btn.disabled = false;
      btn.textContent = '▶ RUN NOW';
    }
  }

  private displayBrief(brief: string, generatedAt?: string): void {
    const ts = generatedAt ? new Date(generatedAt).toLocaleString() : '';
    this.el('brief-meta').innerHTML    = ts ? `Last generated: <strong>${ts}</strong>` : '';
    this.el('brief-body').innerHTML    = renderMarkdown(brief);
    this.el('brief-body').style.display  = '';
    this.el('brief-empty').style.display = 'none';
  }

  private async loadNews(): Promise<void> {
    const feed    = this.el('brief-news-feed');
    const loading = this.el('brief-news-loading');

    if (this.newsFetched) { loading.style.display = 'none'; return; }

    loading.style.display = '';
    loading.textContent   = 'Loading headlines…';

    try {
      let tickers: string[] = [];
      try {
        const d = await fetchSheet('Tickers');
        tickers = (d.rows ?? [])
          .map(r => ((r['Ticker'] ?? r['Symbol']) as string | undefined ?? '').toUpperCase())
          .filter(Boolean);
      } catch { /* fall through to defaults */ }

      if (!tickers.length) tickers = ['AAPL', 'NVDA', 'MSFT', 'PLTR', 'SPY'];

      const articles = await fetchAlpacaNews(tickers.slice(0, 15), 40);
      loading.style.display = 'none';

      if (!articles?.length) {
        feed.innerHTML = '<div style="padding:40px;text-align:center;color:var(--text3);font-family:var(--mono);font-size:12px;">No headlines found — check Alpaca keys in Admin</div>';
        this.newsFetched = true;
        return;
      }

      feed.innerHTML = '';
      for (const a of articles) {
        const item = document.createElement('div');
        item.className = 'brief-news-item';
        item.innerHTML = `
          <div class="brief-news-flag">${a.flag || '📰'}</div>
          <div class="brief-news-content">
            <a href="${esc(a.link)}" target="_blank" rel="noopener" class="brief-news-title">${esc(a.title)}</a>
            ${a.description ? `<div class="brief-news-desc">${esc(a.description)}</div>` : ''}
          </div>
          <div class="brief-news-meta">
            <span class="brief-news-ticker">${esc(a.ticker)}</span>
            <span class="brief-news-source">${esc(a.source)}</span>
            <span class="brief-news-time">${timeAgo(a.date)}</span>
          </div>`;
        feed.appendChild(item);
      }
      this.newsFetched = true;
    } catch (err: unknown) {
      loading.textContent = 'Error loading news: ' + (err instanceof Error ? err.message : String(err));
    }
  }

  private checkWorker(_alert = false): boolean {
    return true; // worker URL is hardcoded in worker.ts
  }
}
